import { he } from 'date-fns/locale/he'

import type { IClientImage } from '@pkg/lib-data'
import { EUserJourneyType } from '@pkg/lib-data'

import { isAbsoluteURL } from '@wl/web-toolkit'

import { getDomainWebsite } from '../data-fetching/ssr'

/**
 * Get empty placeholder image URL with proper domain and base path handling.
 */
const getEmptyImageUrl = (path: string) => {
  const domain = getDomainWebsite()

  // Production: domain is set, return full URL
  if (domain) {
    return `${domain}${path}`
  }

  // Localhost: detect base path from current location
  // SEO app uses base: '/contents', client app has no base
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname
    const basePath = pathname.startsWith('/contents') ? '/contents' : ''

    return `${basePath}${path}`
  }

  // Fallback
  return path
}

export const EMPTY_IMAGE_URLS = {
  [EUserJourneyType.FLIGHTS]: { imageUrl: getEmptyImageUrl('/assets/images/flights/empty-placeholder.svg'), alt: '' },
  [EUserJourneyType.DOMESTIC]: { imageUrl: getEmptyImageUrl('/assets/images/domestic/empty-placeholder.svg'), alt: '' },
  [EUserJourneyType.SHOWS]: { imageUrl: getEmptyImageUrl('/assets/images/shows/empty-placeholder.svg'), alt: '' },
}

export const defineAbsoluteImage = (imageUrl: string) => {
  const domain = getDomainWebsite()

  return isAbsoluteURL(imageUrl) ? imageUrl : `${domain}${imageUrl}`
}

export const defineImages = (args: { imageUrls?: IClientImage[] }): IClientImage[] | null => {
  const { imageUrls } = args

  if (imageUrls && imageUrls.length > 0) {
    return imageUrls.map((item) => {
      return {
        imageUrl: defineAbsoluteImage(item.imageUrl),
        alt: item.alt,
      }
    })
  }

  return null
}

export const DATE_STRING_FORMAT = 'dd.MM'
export const DATE_STRING_FORMAT_FULL = 'dd.MM.yy'
export const DATE_STRING_FORMAT_WEEKEND = 'dd.MM.yy EEEEE'

export const formatOptions = { locale: he }

export const priceFormatter = (price: number) => {
  const int = new Intl.NumberFormat('en-US')

  return int.format(price)
}

/**
 * Generate ALT text for show images when not provided manually.
 * Format: "הופעת {name} ב{areaName}"
 */
export const generateImageAlt = (name: string, areaName: string): string => {
  const showName = name || 'הופעה'
  const area = areaName || 'ישראל'

  return `הופעת ${showName} ב${area}`
}
